import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useMsal, useAccount } from '@azure/msal-react';
import { loginRequest } from '../authConfig';
import { v4 as uuidv4 } from 'uuid';

const ASSISTANT_TYPE = 'hale-general';

const SUGGESTIONS = [
    'Help me draft an email',
    'Explain a complex topic',
    'Analyze some data',
];

const ACCEPTED_MIME_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/csv',
    'text/plain',
    'text/html',
    'text/xml',
    'application/xml',
    'application/json',
    'text/markdown',
    'application/rtf',
    'text/rtf'
].join(',');

const MAX_FILES = 5;
const MAX_FILE_BYTES = 20 * 1024 * 1024;

const FILE_TYPE_ICONS = {
    docx: '📄',
    xlsx: '📊',
    pptx: '📽️',
    csv: '📊',
    html: '🌐',
    txt: '📄'
};

function formatBytes(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const toRelative = (utcDateString) => {
    if (!utcDateString) return '';
    const date = utcDateString.endsWith('Z') ? new Date(utcDateString) : new Date(utcDateString + 'Z');
    const diff = Date.now() - date.getTime();
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
};

function Chat({ navState, onNavigate }) {
    const { instance, accounts } = useMsal();
    const account = useAccount(accounts[0] || {});
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [sessionId, setSessionId] = useState(() => uuidv4());
    const [attachments, setAttachments] = useState([]);
    const [fileError, setFileError] = useState('');
    const [resumeLoading, setResumeLoading] = useState(false);
    const [recentSessions, setRecentSessions] = useState([]);
    const messagesEndRef = useRef(null);
    const textareaRef = useRef(null);
    const fileInputRef = useRef(null);

    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages, scrollToBottom]);

    const getToken = useCallback(async () => {
        try {
            const result = await instance.acquireTokenSilent({ ...loginRequest, account });
            return result.accessToken;
        } catch {
            const result = await instance.acquireTokenRedirect(loginRequest);
            return result?.accessToken;
        }
    }, [instance, account]);

    // Load recent sessions for the strip
    useEffect(() => {
        if (navState?.resumeSessionId) return;
        const fetchRecent = async () => {
            try {
                const token = await getToken();
                const response = await fetch('/api/history/assist/recent', {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (response.ok) {
                    const data = await response.json();
                    setRecentSessions(data);
                }
            } catch (err) {
                console.error('Failed to load recent sessions:', err);
            }
        };
        fetchRecent();
    }, [getToken, navState]);

    // Load session history if resuming
    useEffect(() => {
        if (!navState?.resumeSessionId) return;

        const loadSession = async () => {
            setResumeLoading(true);
            try {
                const token = await getToken();
                const response = await fetch(`/api/history/assist/session/${navState.resumeSessionId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (!response.ok) return;
                const data = await response.json();

                const loadedMessages = [];
                for (const item of data) {
                    loadedMessages.push({
                        id: uuidv4(),
                        role: 'user',
                        content: item.userMessage,
                        timestamp: new Date(item.createdDate)
                    });
                    loadedMessages.push({
                        id: uuidv4(),
                        role: 'assistant',
                        content: item.aiResponse,
                        timestamp: new Date(item.createdDate),
                        responseTimeMs: item.responseTimeMs,
                        tokensUsed: item.tokensUsed
                    });
                }

                setMessages(loadedMessages);
                setSessionId(navState.resumeSessionId);
            } catch (err) {
                console.error('Failed to load session:', err);
            } finally {
                setResumeLoading(false);
            }
        };

        loadSession();
    }, [navState, getToken]);

    const handleNewChat = () => {
        setMessages([]);
        setInput('');
        setAttachments([]);
        setFileError('');
        setSessionId(uuidv4());
    };

    const handleResumeSession = (session) => {
        onNavigate('assistant', { resumeSessionId: session.sessionId });
    };

    const handleFileChange = (e) => {
        setFileError('');
        const selected = Array.from(e.target.files || []);
        const combined = [...attachments, ...selected];

        if (combined.length > MAX_FILES) {
            setFileError(`You can attach a maximum of ${MAX_FILES} files.`);
            e.target.value = '';
            return;
        }

        for (const file of selected) {
            if (file.size > MAX_FILE_BYTES) {
                setFileError(`"${file.name}" exceeds the 20 MB size limit.`);
                e.target.value = '';
                return;
            }
        }

        setAttachments(combined);
        e.target.value = '';
    };

    const removeAttachment = (index) => {
        setAttachments(prev => prev.filter((_, i) => i !== index));
        setFileError('');
    };

    const sendMessage = useCallback(async (text) => {
        if ((!text.trim() && attachments.length === 0) || loading) return;

        const userMessage = {
            id: uuidv4(),
            role: 'user',
            content: text.trim(),
            attachmentNames: attachments.map(f => f.name),
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        setInput('');
        setLoading(true);

        const filesToSend = [...attachments];
        setAttachments([]);
        setFileError('');

        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
        }

        try {
            const token = await getToken();

            const formData = new FormData();
            formData.append('assistantType', ASSISTANT_TYPE);
            formData.append('message', text.trim());
            formData.append('sessionId', sessionId);
            filesToSend.forEach(file => formData.append('files', file));

            const response = await fetch('/api/assist', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });

            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(err.error || `Request failed: ${response.status}`);
            }

            const data = await response.json();

            setMessages(prev => [...prev, {
                id: uuidv4(),
                role: 'assistant',
                content: data.answer,
                timestamp: new Date(),
                responseTimeMs: data.responseTimeMs,
                tokensUsed: data.tokensUsed,
                generatedFileUrl: data.generatedFileUrl,
                generatedFileName: data.generatedFileName,
                generatedFileType: data.generatedFileType
            }]);
        } catch (error) {
            setMessages(prev => [...prev, {
                id: uuidv4(),
                role: 'assistant',
                content: error.message || 'Something went wrong. Please try again.',
                timestamp: new Date(),
                isError: true
            }]);
        } finally {
            setLoading(false);
        }
    }, [loading, sessionId, getToken, attachments]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage(input);
        }
    };

    const handleTextareaChange = (e) => {
        setInput(e.target.value);
        e.target.style.height = 'auto';
        e.target.style.height = Math.min(e.target.scrollHeight, 140) + 'px';
    };

    const userInitials = account?.username?.charAt(0).toUpperCase() || 'U';
    const getInitials = (role) => role === 'user' ? userInitials : 'AI';

    return (
        <div className="chat-layout">
            <div className="chat-header">
                <div>
                    <div className="chat-header-title">AI Assistant</div>
                    <div className="chat-header-subtitle">
                        {navState?.resumeSessionId ? 'Resumed session — all interactions are logged and audited' : 'Governed AI — all interactions are logged and audited'}
                    </div>
                </div>
                <button className="btn-new-chat" onClick={handleNewChat}>+ New Chat</button>
            </div>

            <div className="chat-messages">
                {resumeLoading && (
                    <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d', fontSize: '0.9rem' }}>
                        Loading conversation...
                    </div>
                )}

                {!resumeLoading && messages.length === 0 && !loading && (
                    <div className="welcome-screen">
                        <div className="welcome-icon">🤖</div>
                        <div className="welcome-title">Hale Partnership AI Assistant</div>
                        <div className="welcome-subtitle">
                            Ask me anything. Your interactions are governed, secured, and audited by Hale Partnership infrastructure.
                        </div>

                        {recentSessions.length > 0 && (
                            <div style={{ marginTop: '1.5rem', width: '100%', maxWidth: '600px' }}>
                                <div style={{ fontSize: '0.75rem', color: '#adb5bd', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.6rem' }}>
                                    Recent Conversations
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                                    {recentSessions.map(session => (
                                        <button
                                            key={session.sessionId}
                                            onClick={() => handleResumeSession(session)}
                                            style={{ background: 'white', border: '1px solid #e0e4e8', borderRadius: '8px', padding: '0.6rem 0.9rem', display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer', textAlign: 'left', transition: 'border-color 0.15s', width: '100%' }}
                                            onMouseEnter={e => e.currentTarget.style.borderColor = '#4a9e6d'}
                                            onMouseLeave={e => e.currentTarget.style.borderColor = '#e0e4e8'}
                                        >
                                            <span style={{ fontSize: '1rem', flexShrink: 0 }}>💬</span>
                                            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.85rem', color: '#1a4d2e' }}>
                                                {session.firstMessage}
                                            </span>
                                            <span style={{ fontSize: '0.75rem', color: '#adb5bd', flexShrink: 0 }}>
                                                {toRelative(session.lastActivity)}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="welcome-suggestions" style={{ marginTop: recentSessions.length > 0 ? '1rem' : '1.5rem' }}>
                            {SUGGESTIONS.map((s, i) => (
                                <button key={i} className="suggestion-chip" onClick={() => sendMessage(s)}>
                                    {s}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {messages.map(msg => (
                    <div key={msg.id} className={`message ${msg.role}`}>
                        <div className="message-avatar">{getInitials(msg.role)}</div>
                        <div>
                            <div className={`message-bubble ${msg.isError ? 'error' : ''}`}>
                                {msg.role === 'assistant' ? (
                                    <>
                                        <div dangerouslySetInnerHTML={{ __html: msg.content }} />
                                        {msg.generatedFileUrl && (
                                            <div className="message-attachments" style={{ marginTop: '0.6rem' }}>
                                                <a
                                                    href={msg.generatedFileUrl}
                                                    download={msg.generatedFileName || true}
                                                    className="message-attachment-chip"
                                                    style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}
                                                >
                                                    <span className="attachment-icon">
                                                        {FILE_TYPE_ICONS[msg.generatedFileType] || '📄'}
                                                    </span>
                                                    <span>{msg.generatedFileName || 'Download file'}</span>
                                                </a>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div>
                                        {msg.content && <div>{msg.content}</div>}
                                        {msg.attachmentNames && msg.attachmentNames.length > 0 && (
                                            <div className="message-attachments">
                                                {msg.attachmentNames.map((name, i) => (
                                                    <div key={i} className="message-attachment-chip">
                                                        <span className="attachment-icon">📄</span>
                                                        <span>{name}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div className="message-meta">
                                {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                {msg.responseTimeMs && ` · ${(msg.responseTimeMs / 1000).toFixed(1)}s`}
                                {msg.tokensUsed && ` · ${msg.tokensUsed} tokens`}
                            </div>
                        </div>
                    </div>
                ))}

                {loading && (
                    <div className="message assistant">
                        <div className="message-avatar">AI</div>
                        <div className="message-bubble">
                            <div className="typing-indicator">
                                <div className="typing-dot" />
                                <div className="typing-dot" />
                                <div className="typing-dot" />
                            </div>
                        </div>
                    </div>
                )}

                <div ref={messagesEndRef} />
            </div>

            <div className="chat-input-area">
                {attachments.length > 0 && (
                    <div className="attachment-chips">
                        {attachments.map((file, i) => (
                            <div key={i} className="attachment-chip">
                                <span className="attachment-icon">📄</span>
                                <span className="attachment-name">{file.name}</span>
                                <span className="attachment-size">({formatBytes(file.size)})</span>
                                <button className="attachment-remove" onClick={() => removeAttachment(i)} title="Remove">×</button>
                            </div>
                        ))}
                    </div>
                )}

                {fileError && (
                    <div className="attachment-error">{fileError}</div>
                )}

                <div className="chat-input-box">
                    <button
                        className="btn-attach"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={loading || attachments.length >= MAX_FILES}
                        title="Attach file"
                    >
                        📎
                    </button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept={ACCEPTED_MIME_TYPES}
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                    />
                    <textarea
                        ref={textareaRef}
                        value={input}
                        onChange={handleTextareaChange}
                        onKeyDown={handleKeyDown}
                        placeholder="Ask anything... (Enter to send, Shift+Enter for new line)"
                        rows={1}
                        disabled={loading || resumeLoading}
                    />
                    <button
                        className="btn-send"
                        onClick={() => sendMessage(input)}
                        disabled={(!input.trim() && attachments.length === 0) || loading || resumeLoading}
                    >
                        ↑
                    </button>
                </div>
                <div className="chat-input-hint">
                    Interactions are secured and audited · Hale Partnership Enterprise AI
                </div>
            </div>
        </div>
    );
}

export default Chat;